import { gql, useMutation } from "@apollo/client";
import { Header, useLanguageTranslator } from "../../../../components";
import { AdminView } from "../features/adminView";
import { useApolloQuery } from "../../../../hooks";
import { useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../../../../components/AuthSessionProvider";
import { statusCode, toastTypesKeys } from "../../../../utils/constants";
import { toastHandler } from "../../../../utils/helpers";
import { ToastContainer } from "react-toastify";
import { ConfirmationDialogs } from "../features";

const pendingPracticeDocument = gql`
  query ($limit: Int, $page: Int) {
    pendingPractices(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policy {
          title
        }
        practiceCategory {
          name
        }
        practiceType {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const noPendingDocument = gql`
  query ($limit: Int, $page: Int) {
    noPendingPractices(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policy {
          title
        }
        practiceCategory {
          name
        }
        practiceType {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const practiceStatusUpdate = gql`
  mutation UpdatePracticeStatus($practiceInput: PracticeStatusUpdateInput!) {
    practiceStatusUpdate(input: $practiceInput) {
      clientMutationId
      practice {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policy {
          title
        }
        practiceCategory {
          name
        }
        practiceType {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
    }
  }
`;

function AdminPractices() {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState<any>({
    status: false,
    currId: null,
    moduleCode: 2,
  });
  const [rejectReason, setRejectReason] = useState("");
  const [isError, setIsError] = useState(false);

  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();

  const { data: pending } = useApolloQuery({
    queryDocument: pendingPracticeDocument,
    isPrivateCall: true,
    variables: {
      limit: 8,
    },
  });

  const {
    data: noPending,
    loading,
    refetch,
  } = useApolloQuery({
    queryDocument: noPendingDocument,
    isPrivateCall: true,
    variables: {
      limit: 8,
    },
  });

  const [practiceStatus] = useMutation(practiceStatusUpdate, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  async function onClickHandler(practiceID: number) {
    if (isOpen?.moduleCode === statusCode.approved) {
      try {
        const response = await practiceStatus({
          variables: {
            practiceInput: { id: Number(practiceID), status: "Approved" },
          },
        });
        if (response?.data) {
          setIsOpen({ status: false, currId: null, module: "" });
          toastHandler("Status is Updated", toastTypesKeys.success);
          setTimeout(() => {
            refetch();
          }, 2300);
        }
      } catch (error) {
        console.error("Error:", error);
        setIsOpen({ status: false, currId: null, module: "" });
        setIsError(true);
      }
      return;
    }

    if (!rejectReason)
      return toastHandler("Please enter the Reason", toastTypesKeys.error);

    try {
      const response = await practiceStatus({
        variables: {
          practiceInput: {
            id: Number(practiceID),
            status: "Rejected",
            reason: rejectReason,
          },
        },
      });
      if (response?.data) {
        setIsOpen({ status: false, currId: null, module: "" });
        toastHandler("Status is Updated", toastTypesKeys.success);
        setTimeout(() => {
          refetch();
        }, 2300);
        setRejectReason("");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsOpen({ status: false, currId: null, module: "" });
      setRejectReason("");
      setIsError(true);
    }
  }

  const filteredPendingPracticesList = useMemo(() => {
    if (!inputValue) return pending?.pendingPractices?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return pending?.pendingPractices?.collection?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, pending?.pendingPractices?.collection]);

  const filteredNoPendingPracticesList = useMemo(() => {
    if (!inputValue) return noPending?.noPendingPractices?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return noPending?.noPendingPractices?.collection?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, noPending?.noPendingPractices?.collection]);

  function onAdminActions(id: any, type: any) {
    setIsOpen({ status: true, currId: id, moduleCode: type });
  }

  return (
    <>
      <ToastContainer />
      <Header
        title={translator("Admin Area")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />
      <ConfirmationDialogs
        text="practice"
        viewPath={`/admin/practices/${isOpen?.currId}`}
        isOpen={isOpen}
        onClickHandler={onClickHandler}
        setIsOpen={setIsOpen}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        isError={isError}
        setIsError={setIsError}
      />
      <AdminView
        title={translator("Practices")}
        viewPath="/admin/practices/"
        viewAllSubmissionsPath="/admin/practices/view_all"
        pendingData={filteredPendingPracticesList || []}
        noPendingData={filteredNoPendingPracticesList || []}
        onAdminActions={onAdminActions}
      />
    </>
  );
}

export { AdminPractices };
